import { useEffect, useMemo, useState } from "react";
import { Navigate, useLocation } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { ALLOWED_DISCORD_IDS } from "../pages/Login";

const ROLE_CACHE_KEY = "outfall_admin_role_cache";
const ROLE_CACHE_MAX_AGE = 1000 * 60 * 30;

function getDiscordId(session) {
  return (
    session?.user?.identities?.find((i) => i.provider === "discord")?.id ||
    session?.user?.user_metadata?.provider_id ||
    session?.user?.user_metadata?.sub ||
    session?.user?.identities?.[0]?.identity_data?.sub ||
    null
  );
}

function getCachedRole(discordId) {
  try {
    const raw = localStorage.getItem(ROLE_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw);

    if (parsed.discordId !== discordId) return null;

    if (Date.now() - parsed.checkedAt > ROLE_CACHE_MAX_AGE) {
      localStorage.removeItem(ROLE_CACHE_KEY);
      return null;
    }

    return Boolean(parsed.allowed);
  } catch {
    return null;
  }
}

function setCachedRole(discordId, allowed) {
  try {
    localStorage.setItem(
      ROLE_CACHE_KEY,
      JSON.stringify({
        discordId,
        allowed: Boolean(allowed),
        checkedAt: Date.now(),
      }),
    );
  } catch {
    // ignore
  }
}

export default function ProtectedRoute({ children }) {
  const { session } = useAuth();
  const location = useLocation();

  const discordId = useMemo(() => getDiscordId(session), [session]);

  const [checked, setChecked] = useState(false);
  const [hasAccess, setHasAccess] = useState(false);

  useEffect(() => {
    let cancelled = false;

    async function checkAccess() {
      if (session === undefined) return;

      if (!session || !discordId) {
        setChecked(true);
        setHasAccess(false);
        return;
      }

      if (ALLOWED_DISCORD_IDS.includes(discordId)) {
        setCachedRole(discordId, true);
        setHasAccess(true);
        setChecked(true);
        return;
      }

      const cached = getCachedRole(discordId);

      if (cached !== null) {
        setHasAccess(cached);
        setChecked(true);
        return;
      }

      try {
        const res = await fetch("/api/check-staff-role", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            discord_id: discordId,
            secret: process.env.REACT_APP_DM_WEBHOOK_SECRET,
          }),
        });

        const data = await res.json().catch(() => ({}));
        const allowed = Boolean(data.hasRole);

        if (!cancelled) {
          setCachedRole(discordId, allowed);
          setHasAccess(allowed);
          setChecked(true);
        }
      } catch {
        if (!cancelled) {
          setHasAccess(false);
          setChecked(true);
        }
      }
    }

    setChecked(false);
    checkAccess();

    return () => {
      cancelled = true;
    };
  }, [session, discordId]);

  if (session === undefined || !checked) {
    return null;
  }

  if (!session) {
    return (
      <Navigate
        to={`/login?returnTo=${encodeURIComponent(
          location.pathname + location.search,
        )}`}
        replace
      />
    );
  }

  if (!hasAccess) {
    return <Navigate to="/login?error=unauthorized" replace />;
  }

  return children;
}
